/**
 * Where to send someone once they are signed in, when a route interrupted
 * them to ask for it.
 *
 * The invitation link is the case that matters: an invitee who already has a
 * Finopay login is asked to sign in, and must land back on the invitation so
 * it can be accepted. The path has to survive 2FA and profile selection,
 * because each of those navigates on its own and router state does not travel
 * with them - so it is parked in sessionStorage and taken back exactly once.
 *
 * Only same-app paths are kept: a value that could leave the app (`//evil`,
 * `https://…`) is dropped rather than followed.
 */

const STORAGE_KEY = "ZENITH_RETURN_TO";

const isInAppPath = (path: unknown): path is string =>
  typeof path === "string" && path.startsWith("/") && !path.startsWith("//");

/** Park an in-app path; anything else is ignored and leaves the parked one. */
export const rememberReturnTo = (path: unknown): void => {
  if (isInAppPath(path)) {
    sessionStorage.setItem(STORAGE_KEY, path);
  }
};

/** The parked path, without consuming it. */
export const peekReturnTo = (): string | undefined => {
  const path = sessionStorage.getItem(STORAGE_KEY);
  return isInAppPath(path) ? path : undefined;
};

/** The parked path, consumed so it is honoured only once. */
export const takeReturnTo = (): string | undefined => {
  const path = peekReturnTo();
  sessionStorage.removeItem(STORAGE_KEY);
  return path;
};
